import { inject, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, map, Observable, OperatorFunction, take } from 'rxjs';
import { CoreStore } from '@core/store/core.store';

/**
 * Emits once (and completes) when the session user retrieval is done, successfully or not.
 * Replaces the pattern
 *   store.select(isRetrieveUserCompleted).pipe(skipWhile(v => !v), take(1))
 *
 * Requires an injection context: guards, resolvers and field initializers of
 * directives/components have one. Anywhere else, pass the `injector` explicitly.
 */
export const userRetrieved$ = (injector?: Injector): Observable<void> => {
  const store = injector ? injector.get(CoreStore) : inject(CoreStore);

  return toObservable(store.retrieveUserCompleted, injector ? { injector } : undefined).pipe(
    filter(Boolean),
    take(1),
    map(() => void 0)
  );
};

/**
 * Pairs each value with the session user id read at emission time, dropping the value when
 * no user is loaded: feature stores never call the backend with a placeholder id.
 */
export const withSessionUserId = <T>(coreStore: InstanceType<typeof CoreStore>): OperatorFunction<T, [T, number]> =>
  source$ => source$.pipe(
    map((value): [T, number | undefined] => [value, coreStore.userId()]),
    filter((pair): pair is [T, number] => !!pair[1])
  );
